import { Injectable, OnDestroy, inject, Provider, APP_INITIALIZER } from '@angular/core';
import { DA_SERVICE_TOKEN, ITokenService } from '@delon/auth';
import { _HttpClient } from '@delon/theme';
import { environment } from '@env/environment';
import { interval, Subscription, fromEvent, merge } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

const TOKEN_STORAGE_KEY = 'qca_token';
const CHECK_INTERVAL = 60000; // 1 minute
const EXPIRY_THRESHOLD = 5 * 60 * 1000; // 5 minutes in milliseconds
const IDLE_THRESHOLD = 60000; // 1 minute in milliseconds

/**
 * Used for token refresh service initialization
 * Ensures the token refresh service is initialized when the app starts
 */
export function provideTokenRefresh(): Provider[] {
  return [
    TokenRefreshService, // Explicitly provide the service
    {
      provide: APP_INITIALIZER,
      useFactory: (tokenRefreshService: TokenRefreshService) => {
        // Service is injected via deps, which forces Angular to create it
        // The constructor will initialize activity tracking and token expiry check
        // We just need to return a function that returns a promise
        return () => {
          // Service is already initialized in constructor at this point
          return Promise.resolve();
        };
      },
      deps: [TokenRefreshService],
      multi: true
    }
  ];
}

@Injectable()
export class TokenRefreshService implements OnDestroy {
  private checkSubscription?: Subscription;
  private activitySubscription?: Subscription;
  private lastActivityTime: number = Date.now();
  private isRefreshing: boolean = false;
  private tokenService = inject(DA_SERVICE_TOKEN);
  private http = inject(_HttpClient);

  constructor() {
    this.log('TokenRefreshService initialized');
    this.startActivityTracking();
    this.startTokenExpiryCheck();
  }

  ngOnDestroy(): void {
    this.stopActivityTracking();
    this.stopTokenExpiryCheck();
  }

  /**
   * Start tracking user activity (mouse, keyboard, clicks, scrolls)
   */
  private startActivityTracking(): void {
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    const eventStreams = events.map(event => fromEvent(document, event));
    this.activitySubscription = merge(...eventStreams)
      .pipe(debounceTime(100))
        .subscribe(() => {
        this.lastActivityTime = Date.now();
      });
  }

  private stopActivityTracking(): void {
    if (this.activitySubscription) {
      this.activitySubscription.unsubscribe();
    }
  }

  /**
   * Check token expiry every minute
   */
  private startTokenExpiryCheck(): void {
    this.checkSubscription = interval(CHECK_INTERVAL).subscribe(() => {
      this.checkAndRefreshToken();
    });
    
    // Also check immediately on startup
    this.checkAndRefreshToken();
  }

  private stopTokenExpiryCheck(): void {
    if (this.checkSubscription) {
      this.checkSubscription.unsubscribe();
    }
  }

  /**
   * Check if token needs to be refreshed and perform refresh if conditions are met
   */
  private checkAndRefreshToken(): void {
    // Always read token from localStorage to get the latest value (not from memory cache)
    const tokenData = this.getTokenFromStorage();
    
    // Only work if user is logged in (has a valid token)
    if (!tokenData || !tokenData.token) {
      this.log('Token check skipped: No token available (user not logged in)');
      return; // No token available - user not logged in
    }

    // Don't refresh if already refreshing
    if (this.isRefreshing) {
      this.log('Token check skipped: Refresh already in progress');
      return;
    }

    // Check if user is active (not idle in the last minute) - check this first before expiry calculations
    const now = Date.now();
    const timeSinceLastActivity = now - this.lastActivityTime;
    const formattedActivityTime = this.formatTimeRemaining(timeSinceLastActivity);
    if (timeSinceLastActivity > IDLE_THRESHOLD) {
      this.log(`Token check skipped: User is idle (${formattedActivityTime} since last activity)`);
      return; // User is idle, don't refresh
    }

    // Check if token is expired or will expire soon using localStorage value
    const expiryTime = tokenData.expired || 0;
    const timeUntilExpiry = expiryTime - now;
    const formattedTime = this.formatTimeRemaining(timeUntilExpiry);

    this.log(`Token check: Expires in ${formattedTime} (${new Date(expiryTime).toLocaleTimeString()})`);

    // Only renew the token when there is a valid token that has not expired yet
    if (timeUntilExpiry <= 0) {
      this.log('Token check skipped: Token is already expired');
      return; // Token is already expired, don't try to refresh
    }

    // Only refresh if token will expire in 5 minutes or less (but not already expired)
    if (timeUntilExpiry > EXPIRY_THRESHOLD) {
      this.log(`Token check skipped: Token still valid for more than 5 minutes (${formattedTime} remaining)`);
      return; // Token is still valid for more than 5 minutes
    }

    // Perform token refresh (token is valid and will expire soon, user is active)
    this.log(`Token refresh conditions met: Token expires in ${formattedTime}, user active (${formattedActivityTime} since last activity)`);
    this.refreshToken();
  }

  /**
   * Get token from localStorage (always read fresh, never use cached value)
   */
  private getTokenFromStorage(): any {
    try {
      const storedValue = localStorage.getItem(TOKEN_STORAGE_KEY);
      if (!storedValue) {
        return null;
      }
      return JSON.parse(storedValue);
    } catch (error) {
      console.error('Error reading token from storage:', error);
      return null;
    }
  }

  /**
   * Refresh token using refresh token API
   */
  private refreshToken(): void {
    if (this.isRefreshing) {
      this.log('Token refresh skipped: Already refreshing');
      return; // Already refreshing
    }

    this.isRefreshing = true;
    this.log('🔄 Starting token refresh...');

    this.http.post('/api/auth/refresh', null, { _silent: true }).subscribe({
      next: (response: any) => {
        this.isRefreshing = false;

        if (response && response.token) {
          // Update token in token service (this will also update localStorage)
          this.tokenService.set(response);
          // Update last activity time to prevent immediate re-check
          this.lastActivityTime = Date.now();
          
          const newExpiryTime = response.expired || 0;
          const newExpiryDate = new Date(newExpiryTime);
          this.log(`✅ Token refresh successful! New token expires at ${newExpiryDate.toLocaleString()}`);
        } else {
          this.log('⚠️ Token refresh response missing token');
        }
      },
      error: (error) => {
        console.error('Token refresh failed:', error);
        this.log(`❌ Token refresh failed: ${error.message || 'Unknown error'}`);
        this.isRefreshing = false;
      }
    });
  }

  /**
   * Format time remaining in a human-readable format
   * - Less than 1 minute: "X seconds"
   * - Less than 1 hour: "X minutes X seconds"
   * - 1 hour or more: "X hours X minutes X seconds"
   */
  private formatTimeRemaining(milliseconds: number): string {
    if (milliseconds <= 0) {
      return '0 seconds';
    }

    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const parts: string[] = [];

    if (hours > 0) {
      parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
    }

    if (minutes > 0) {
      parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
    }

    if (seconds > 0 || parts.length === 0) {
      parts.push(`${seconds} ${seconds === 1 ? 'second' : 'seconds'}`);
    }

    return parts.join(' ');
  }

  /**
   * Log message only in development mode
   */
  private log(message: string, ...args: any[]): void {
    if (!environment.production) {
      console.log(`[TokenRefresh] ${message}`, ...args);
    }
  }

}

